import { useEffect, useRef, useState } from 'react';
import { AudioLines, FileText, Loader2, Mic, MicOff, PhoneOff, Volume2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLetsTalkVoice } from '../../hooks/useLetsTalkVoice';
import { track } from '../../lib/analytics';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

export function LetsTalkWidget() {
  const [open, setOpen] = useState(false);
  // The dialog opens on Start conversation: the setup-guide link above it would otherwise be the
  // first focusable element, and Radix focuses that.
  const startRef = useRef<HTMLButtonElement>(null);
  // Set when another surface opened the dialog, so closing returns focus there rather than to the
  // floating trigger (Radix's default) at the end of the page.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const voice = useLetsTalkVoice();
  const active = voice.status === 'connected';
  const busy = voice.status === 'connecting' || voice.status === 'closing';
  const status = voice.status === 'connecting' ? 'Connecting…'
    : voice.status === 'closing' ? 'Ending conversation…'
      : active ? voice.isMuted ? 'Microphone muted' : 'Listening — speak naturally'
        : 'Ready when you are';

  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) void voice.stop();
    else track({ name: 'feature_opened', properties: { feature: 'lets_talk' } });
  };

  // Other surfaces (AI Chat voice, for questions across deals) open the dialog without starting
  // the microphone — the user still chooses Start conversation.
  useEffect(() => {
    const openFromElsewhere = (event: Event) => {
      const opener = (event as CustomEvent<{ returnFocus?: unknown }>).detail?.returnFocus;
      returnFocusRef.current = opener instanceof HTMLElement ? opener : null;
      setOpen(true);
      track({ name: 'feature_opened', properties: { feature: 'lets_talk' } });
    };
    window.addEventListener('returncatalyst:open-lets-talk', openFromElsewhere);
    return () => window.removeEventListener('returncatalyst:open-lets-talk', openFromElsewhere);
  }, []);

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="fixed bottom-24 right-4 z-40 flex items-center gap-2 rounded-full border border-border bg-primary px-5 py-3 text-primary-foreground shadow-lg transition-shadow hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:bottom-6 lg:right-6"
          aria-label="Lets Talk — open voice knowledge assistant"
        >
          <AudioLines className="h-5 w-5" aria-hidden="true" />
          <span className="text-sm font-medium">Lets Talk</span>
        </button>
      </DialogTrigger>
      <DialogContent
        onOpenAutoFocus={(event) => {
          const start = startRef.current;
          if (!start) return;
          event.preventDefault();
          start.focus();
        }}
        onCloseAutoFocus={(event) => {
          const opener = returnFocusRef.current;
          returnFocusRef.current = null;
          if (opener?.isConnected) {
            event.preventDefault();
            opener.focus();
          }
        }}
        className="flex max-h-[85dvh] w-[calc(100%-2rem)] max-w-xl flex-col gap-5 overflow-hidden rounded-xl bg-card text-card-foreground [&>button]:text-foreground [&>button]:data-[state=open]:bg-muted [&>button]:data-[state=open]:text-foreground">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AudioLines className="h-6 w-6 text-primary" aria-hidden="true" />
            Lets Talk
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm leading-relaxed text-foreground">
            Ask about any deal, company, or industry in your organization’s knowledge base.
            Answers use only documents you’re allowed to access.
          </DialogDescription>
          {/* Lets Talk answers from the organization's documents; how-to questions belong to the setup guide. */}
          <Button
            asChild
            variant="link"
            className="h-auto min-h-6 justify-start self-start whitespace-normal px-0 py-1 text-left text-xs normal-case tracking-normal underline"
          >
            <Link to="/welcome?ask=1" onClick={() => changeOpen(false)}>
              How-to questions about ReturnCatalyst? Ask your setup guide
            </Link>
          </Button>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted p-3 text-sm text-foreground" role="status">
          {busy || voice.isSearching
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            : voice.isMuted
              ? <MicOff className="h-4 w-4" aria-hidden="true" />
              : <Mic className="h-4 w-4" aria-hidden="true" />}
          <span>{voice.isSearching ? 'Searching your knowledge base…' : status}</span>
        </div>

        {voice.error && (
          <p role="alert" className="rounded-lg border border-destructive/40 bg-card p-3 text-sm text-foreground">
            {voice.error}
          </p>
        )}

        <audio ref={voice.audioRef} autoPlay />
        {voice.audioPlaybackBlocked && (
          <Button variant="outline" onClick={() => void voice.resumeAudio()}>
            <Volume2 className="mr-2 h-4 w-4" aria-hidden="true" />Enable speaker audio
          </Button>
        )}

        <div className="min-h-0 space-y-4 overflow-y-auto">
          {voice.transcripts.length === 0 ? (
            <div className="space-y-2 py-3 text-sm leading-relaxed text-foreground">
              <p className="font-medium">Start with a question</p>
              <p>“What do our documents say about the healthcare industry?”</p>
              <p>“Compare the companies we’ve reviewed in software.”</p>
              <p className="text-xs text-muted-foreground">If the knowledge base doesn’t support an answer, Lets Talk will say so.</p>
            </div>
          ) : (
            <section aria-label="Conversation transcript" className="space-y-3">
              {voice.transcripts.map((entry) => (
                <div key={entry.id} className="rounded-lg border border-border bg-card p-3">
                  <p className="mb-1 text-xs font-semibold text-foreground">{entry.role === 'user' ? 'You' : 'Lets Talk'}</p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{entry.text}</p>
                </div>
              ))}
            </section>
          )}

          {voice.citations.length > 0 && (
            <section aria-label="Sources" className="space-y-2 border-t border-border pt-3">
              <h3 className="text-sm font-semibold">Sources</h3>
              {voice.citations.map((citation) => (
                <a
                  key={`${citation.projectId}-${citation.documentId}-${citation.sourceNumber}`}
                  href={`/project/${encodeURIComponent(citation.projectId)}?tab=documents`}
                  onClick={() => changeOpen(false)}
                  className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <FileText className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 break-words">
                    {citation.fileName}
                    {citation.pageNumber != null ? ` · Page ${citation.pageNumber}` : ''}
                  </span>
                </a>
              ))}
            </section>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 border-t border-border pt-4">
          {active || busy ? (
            <>
              <Button variant="outline" disabled={!active} onClick={voice.toggleMute} aria-pressed={voice.isMuted}>
                {voice.isMuted ? <Mic className="mr-2 h-4 w-4" aria-hidden="true" /> : <MicOff className="mr-2 h-4 w-4" aria-hidden="true" />}
                {voice.isMuted ? 'Unmute' : 'Mute'}
              </Button>
              <Button variant="outline" disabled={voice.status === 'closing'} onClick={() => void voice.stop()}>
                <PhoneOff className="mr-2 h-4 w-4" aria-hidden="true" />
                {voice.status === 'connecting' ? 'Cancel' : 'End conversation'}
              </Button>
            </>
          ) : (
            <Button ref={startRef} onClick={() => void voice.start()}>
              <Mic className="mr-2 h-4 w-4" aria-hidden="true" />Start conversation
            </Button>
          )}
          <p className="basis-full text-xs text-muted-foreground">Microphone access starts only when you choose Start conversation.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
